import React, { useState, useEffect } from "react";
import { Card, Button, Input, Typography, Row, Col } from "antd";
import {
  ArrowLeftOutlined,
  MailOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  useSendOtpForApplicationMutation,
  useVerifyOtpForApplicationMutation,
} from "../../redux/rtk/apiRtk";
import { primaryColor } from "../../utils/constants";
import { ToastContainer, toast } from "react-toastify";

const { Title, Text } = Typography;

export const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const [sendOtp, { isLoading: isSending }] =
    useSendOtpForApplicationMutation();
  const [verifyOtp, { isLoading: isVerifying }] =
    useVerifyOtpForApplicationMutation();

  useEffect(() => {
    if (!email) {
      navigate("/become-consultant");
      return;
    }

    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      const result = await verifyOtp({ email, otp }).unwrap();

      toast.success("OTP verified successfully!");
      navigate("/become-consultant/success", {
        state: {
          email,
          applicationId: result?._id,
        },
      });
    } catch (error) {
      toast.error(error?.data?.message || "Invalid OTP. Please try again.");
    }
  };

  const handleResendOtp = async () => {
    try {
      await sendOtp(email).unwrap();
      toast.success("OTP sent successfully!");
      setCountdown(60);
      setCanResend(false);
      setOtp("");

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to send OTP");
    }
  };

  const handleOtpChange = (value) => {
    if (value.length <= 6 && /^\d*$/.test(value)) {
      setOtp(value);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <ToastContainer />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/become-consultant")}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-300"
            size="large"
          >
            Back
          </Button>
        </motion.div>

        <Card
          className="rounded-3xl shadow-xl border-0 overflow-hidden"
          style={{ boxShadow: "0 20px 60px rgba(164, 1, 101, 0.15)" }}
        >
          {/* Header */}
          <div
            className="text-center py-8 px-6 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 50%, ${primaryColor}BB 100%)`,
            }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white opacity-10 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>

            <div
              className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <SafetyOutlined style={{ fontSize: "36px", color: "white" }} />
            </div>

            <Title
              level={2}
              style={{ color: "white", margin: 0, fontWeight: 600 }}
            >
              Verify OTP
            </Title>
            <Text
              style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "16px" }}
            >
              We've sent a 6-digit code to
            </Text>
            <div className="flex items-center justify-center mt-2">
              <MailOutlined style={{ color: "white", marginRight: "8px" }} />
              <Text
                style={{ color: "white", fontSize: "18px", fontWeight: 500 }}
              >
                {email}
              </Text>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="text-center mb-8">
              <Text type="secondary" className="text-base">
                Enter the verification code to continue with your consultant
                application
              </Text>
            </div>

            {/* OTP Input */}
            <div className="mb-6">
              <Input
                value={otp}
                onChange={(e) => handleOtpChange(e.target.value)}
                placeholder="Enter 6-digit OTP"
                size="large"
                className="text-center text-2xl font-mono tracking-widest rounded-xl"
                style={{
                  height: "60px",
                  fontSize: "24px",
                  letterSpacing: "8px",
                }}
                maxLength={6}
                onPressEnter={handleVerifyOtp}
              />
            </div>

            {/* Verify Button */}
            <Button
              type="primary"
              size="large"
              block
              onClick={handleVerifyOtp}
              loading={isVerifying}
              disabled={otp.length !== 6}
              className="rounded-xl h-12 font-semibold text-lg mb-6"
              style={{
                backgroundColor: primaryColor,
                borderColor: primaryColor,
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
              }}
            >
              {isVerifying ? "Verifying..." : "Verify OTP"}
            </Button>

            {/* Resend Section */}
            <div className="text-center">
              <Text type="secondary">Didn't receive the code? </Text>
              {canResend ? (
                <Button
                  type="link"
                  onClick={handleResendOtp}
                  loading={isSending}
                  className="p-0 font-semibold"
                  style={{ color: primaryColor }}
                >
                  Resend OTP
                </Button>
              ) : (
                <Text style={{ color: primaryColor, fontWeight: 500 }}>
                  Resend in {countdown}s
                </Text>
              )}
            </div>
          </div>
        </Card>

        {/* Security Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-6 text-center"
        >
          <Text type="secondary" className="text-sm">
            🔒 Your information is secure and encrypted
          </Text>
        </motion.div>
      </motion.div>

      <style jsx="true">{`
        .ant-input:focus,
        .ant-input-focused {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }

        .ant-input:hover {
          border-color: ${primaryColor}80 !important;
        }
      `}</style>
    </div>
  );
};
